import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import ParkingLotMap from '../../components/ParkingLotMap';
import LoadingState from '../../components/common/LoadingState';
import ErrorState from '../../components/common/ErrorState';
import {
    fetchPublicParkingLotFeedbackProposals,
    getParkingLotByIdLegacy,
    patchPublicParkingLotFeedbackProposal,
    acceptPublicParkingLotFeedbackProposal,
    archiveDeleteParkingLot,
    useCities,
} from '../../lib/api';
import type { PublicParkingLotFeedbackItem } from '../../lib/api';
import type { ParkingLot } from '../../lib/types';
import type { GoogleMapsLatLng } from '../../lib/utils';
import { convertGeoJSONToGoogleMaps, convertGoogleMapsToGeoJSON } from '../../lib/utils';

const DEFAULT_CITY = 'aalborg';

const emptyCoords: GoogleMapsLatLng[] = [];

const ParkingLotFeedbackTab: React.FC = () => {
    const { cities } = useCities();
    const [city, setCity] = useState(DEFAULT_CITY);
    const [selected, setSelected] = useState<PublicParkingLotFeedbackItem | null>(null);
    const [parentLot, setParentLot] = useState<ParkingLot | null>(null);
    const [loadingParent, setLoadingParent] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState<{
        name: string;
        address: string;
        parking_type: string;
        spaces: string;
        cleanedPriceZone: string;
        paidHours: string;
        freeDurationRules: string;
        restriction_description: string;
        comment: string;
    } | null>(null);
    const [draftCoords, setDraftCoords] = useState<GoogleMapsLatLng[]>(emptyCoords);

    const { data, error, isLoading, mutate } = useSWR(
        ['public-parking-lot-feedback', city],
        () => fetchPublicParkingLotFeedbackProposals('pending', city)
    );

    const proposals = data ?? [];

    const onSelectRow = useCallback(async (item: PublicParkingLotFeedbackItem) => {
        setSelected(item);
        setActionError(null);
        // Initialize editable draft from proposal
        setDraft({
            name: item.proposal.name || '',
            address: item.proposal.address || '',
            parking_type: item.proposal.parking_type || 'free',
            spaces: item.proposal.spaces != null ? String(item.proposal.spaces) : '',
            cleanedPriceZone: item.proposal.additionalProperties?.cleaned_price_zone || '',
            paidHours:
                // prefer structured paid_hours if present
                (item.proposal.additionalProperties as any)?.paid_hours || item.proposal.paidHours || '',
            freeDurationRules: item.proposal.additionalProperties?.free_duration_rules || '',
            restriction_description: item.proposal.restriction_description || '',
            comment: item.proposal.comment || '',
        });
        setDraftCoords(convertGeoJSONToGoogleMaps(item.proposal.geometry as any));

        if (item.kind === 'edit' && item.parentLotId) {
            setLoadingParent(true);
            try {
                const p = await getParkingLotByIdLegacy(item.parentLotId);
                setParentLot({ ...p, _id: p.lotId || p._id });
            } catch {
                setParentLot(null);
                setActionError('Kunne ikke hente produktions-plads (tjek lotId).');
            } finally {
                setLoadingParent(false);
            }
        } else {
            setParentLot(null);
        }
    }, []);

    useEffect(() => {
        if (!selected) {
            setDraft(null);
            setDraftCoords(emptyCoords);
        }
    }, [selected]);

    const proposalAsLot: ParkingLot | null = useMemo(() => {
        if (!selected || !draft) return null;
        const parkingType = draft.parking_type || 'free';
        return {
            _id: selected.lotId,
            lotId: selected.lotId,
            name: draft.name || selected.proposal.name || 'Forslag',
            geometry: (convertGoogleMapsToGeoJSON(draftCoords) as any) || selected.proposal.geometry,
            parking_type: parkingType,
            address: draft.address || selected.proposal.address || '',
            restriction_description: draft.restriction_description || undefined,
            feedback: [],
        };
    }, [selected, draft, draftCoords]);

    const mapLots: ParkingLot[] = useMemo(() => {
        if (!selected || !proposalAsLot) return [];
        if (parentLot && selected.kind === 'edit') {
            return [parentLot, proposalAsLot];
        }
        return [proposalAsLot];
    }, [selected, proposalAsLot, parentLot]);

    const polygonStylesByLotId = useMemo(() => {
        if (!selected || !proposalAsLot) return undefined;
        const styles: Record<string, Partial<google.maps.PolygonOptions>> = {
            [proposalAsLot.lotId]: {
                strokeColor: '#7c3aed',
                fillColor: '#a78bfa',
                fillOpacity: 0.35,
                strokeWeight: 3,
            },
        };
        if (parentLot) {
            styles[parentLot.lotId] = {
                strokeColor: '#00800A',
                fillColor: '#22c55e',
                fillOpacity: 0.22,
                strokeWeight: 2,
            };
        }
        return styles;
    }, [selected, proposalAsLot, parentLot]);

    const canAcceptEdit = selected?.kind === 'edit' && !!parentLot;
    const acceptDisabled =
        !selected ||
        busy ||
        (selected.kind === 'edit' && !canAcceptEdit) ||
        (selected.kind === 'new' && !proposalAsLot);

    const handleAccept = async () => {
        if (!selected || !draft) return;
        setBusy(true);
        setActionError(null);
        try {
            const geometry = convertGoogleMapsToGeoJSON(draftCoords) as any;
            if (!geometry) {
                throw new Error('Polygon mangler/er ugyldig (ret polygonet og prøv igen).');
            }

            await acceptPublicParkingLotFeedbackProposal(selected.id, {
                geometry,
                name: draft.name || null,
                address: draft.address || null,
                city: selected.proposal.city || city || null,
                parking_type: draft.parking_type || null,
                spaces: draft.spaces || null,
                additionalProperties: {
                    free_duration_rules: draft.freeDurationRules || null,
                    cleaned_price_zone: draft.cleanedPriceZone || null,
                    paid_hours: draft.paidHours || null,
                },
                restriction_description: draft.restriction_description || null,
            });
            await mutate();
            setSelected(null);
            setParentLot(null);
        } catch (e: unknown) {
            setActionError(e instanceof Error ? e.message : 'Handling fejlede');
        } finally {
            setBusy(false);
        }
    };

    const metaRows = useMemo(() => {
        if (!selected) return [];
        const p = selected.proposal;
        const d = draft;
        const rows: Array<{ label: string; from?: string; to?: string }> = [];
        const push = (label: string, from: any, to: any) => {
            const a = from == null || from === '' ? '—' : String(from);
            const b = to == null || to === '' ? '—' : String(to);
            if (a === b) return;
            rows.push({ label, from: a, to: b });
        };
        push('Navn', parentLot?.name, d?.name ?? p.name);
        push('Adresse', parentLot?.address, d?.address ?? p.address);
        push('Type', parentLot?.parking_type, d?.parking_type ?? p.parking_type);
        push('Kapacitet', (parentLot as any)?.spaces, d?.spaces ?? p.spaces);
        push(
            'cleaned_price_zone',
            (parentLot as any)?.additionalProperties?.cleaned_price_zone || (parentLot as any)?.priceZone || (parentLot as any)?.priceZoneLegacy,
            d?.cleanedPriceZone ?? p.additionalProperties?.cleaned_price_zone
        );
        push(
            'paid_hours',
            (parentLot as any)?.additionalProperties?.paid_hours,
            d?.paidHours ?? (p.additionalProperties as any)?.paid_hours ?? p.paidHours
        );
        push(
            'Gratis regler',
            (parentLot as any)?.additionalProperties?.free_duration_rules,
            d?.freeDurationRules ?? p.additionalProperties?.free_duration_rules
        );
        push('Restriktion', parentLot?.restriction_description, d?.restriction_description ?? p.restriction_description);
        return rows;
    }, [selected, parentLot, draft]);

    const handleReject = async () => {
        if (!selected) return;
        setBusy(true);
        setActionError(null);
        try {
            await patchPublicParkingLotFeedbackProposal(selected.id, 'rejected');
            await mutate();
            setSelected(null);
            setParentLot(null);
        } catch (e: unknown) {
            setActionError(e instanceof Error ? e.message : 'Afvisning fejlede');
        } finally {
            setBusy(false);
        }
    };

    const handleArchiveDeleteParent = async () => {
        if (!selected || selected.kind !== 'edit' || !parentLot || busy) return;
        const label = parentLot.name || parentLot.lotId || parentLot._id;
        const ok = window.confirm(
            `Er du sikker på at du vil arkivere og slette produktions-pladsen "${label}"?\n\nPladsen flyttes til replacedparkinglots med timestamp.`
        );
        if (!ok) return;
        setBusy(true);
        setActionError(null);
        try {
            await archiveDeleteParkingLot(parentLot.lotId || parentLot._id, `feedback-review:${selected.id}`);
            await mutate();
            setParentLot(null);
        } catch (e: unknown) {
            setActionError(e instanceof Error ? e.message : 'Sletning fejlede');
        } finally {
            setBusy(false);
        }
    };

    if (isLoading && !data) {
        return <LoadingState message="Henter offentlige forslag…" />;
    }

    if (error) {
        return (
            <ErrorState
                error={error instanceof Error ? error.message : 'Kunne ikke hente forslag'}
                onRetry={() => mutate()}
            />
        );
    }

    return (
        <div className="flex flex-col lg:flex-row h-full min-h-[480px] gap-4 p-4">
            <div className="lg:w-[420px] flex flex-col border border-gray-200 rounded-lg bg-white overflow-hidden">
                <div className="p-3 border-b border-gray-100 flex items-center gap-2">
                    <label className="text-sm text-gray-600 flex items-center gap-2">
                        By
                        <select
                            value={city}
                            onChange={(e) => {
                                setCity(e.target.value);
                                setSelected(null);
                                setParentLot(null);
                            }}
                            className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                        >
                            {cities.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <span className="text-xs text-gray-500">{proposals.length} afventer</span>
                </div>
                <div className="overflow-auto flex-1">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-left text-gray-600">
                            <tr>
                                <th className="px-3 py-2 font-medium">Tid</th>
                                <th className="px-3 py-2 font-medium">Type</th>
                                <th className="px-3 py-2 font-medium">Forslag id</th>
                                <th className="px-3 py-2 font-medium">Forælder</th>
                            </tr>
                        </thead>
                        <tbody>
                            {proposals.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-3 py-6 text-center text-gray-500">
                                        Ingen afventende forslag for denne by.
                                    </td>
                                </tr>
                            ) : (
                                proposals.map((p) => (
                                    <tr
                                        key={p.id}
                                        onClick={() => void onSelectRow(p)}
                                        className={`cursor-pointer border-t border-gray-100 hover:bg-blue-50/50 ${
                                            selected?.id === p.id ? 'bg-blue-50' : ''
                                        }`}
                                    >
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                            {new Date(p.submittedAt).toLocaleString('da-DK')}
                                        </td>
                                        <td className="px-3 py-2">{p.kind === 'new' ? 'Ny' : 'Ret'}</td>
                                        <td className="px-3 py-2 font-mono text-xs">{p.lotId}</td>
                                        <td className="px-3 py-2 font-mono text-xs">
                                            {p.parentLotId ?? '—'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex-1 flex flex-col min-h-[520px] border border-gray-200 rounded-lg bg-white overflow-hidden">
                <div className="p-3 border-b border-gray-100 flex flex-wrap gap-2 items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Kort (diff)</h2>
                        <p className="text-xs text-gray-500">
                            Grøn = produktion · Lilla = forslag. Godkend opdaterer produktion via eksisterende API,
                            derefter markeres forslaget accepteret.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={acceptDisabled}
                            onClick={() => void handleAccept()}
                            className="px-4 py-2 rounded-md bg-green-600 text-white text-sm font-medium disabled:opacity-40"
                        >
                            {busy ? 'Arbejder…' : 'Godkend'}
                        </button>
                        <button
                            type="button"
                            disabled={!selected || busy}
                            onClick={() => void handleReject()}
                            className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium disabled:opacity-40"
                        >
                            Afvis
                        </button>
                        <button
                            type="button"
                            disabled={!selected || selected.kind !== 'edit' || !parentLot || busy}
                            onClick={() => void handleArchiveDeleteParent()}
                            className="px-4 py-2 rounded-md border border-red-300 text-red-700 bg-red-50 text-sm font-medium disabled:opacity-40"
                            title="Arkivér og slet produktions-pladsen"
                        >
                            Slet plads
                        </button>
                    </div>
                </div>
                {actionError && (
                    <div className="mx-3 mt-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">
                        {actionError}
                    </div>
                )}
                {loadingParent && (
                    <div className="p-3 text-sm text-gray-500">Henter produktions-plads…</div>
                )}
                <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[1.2fr_1fr] gap-0">
                    <div className="min-h-[420px] xl:min-h-0 border-b xl:border-b-0 xl:border-r border-gray-100 relative">
                        {selected && proposalAsLot ? (
                            <ParkingLotMap
                                parkingLots={mapLots}
                                selectedPlace={proposalAsLot}
                                editMode={true}
                                creatingNew={false}
                                currentCoordinates={draftCoords}
                                polygonStylesByLotId={polygonStylesByLotId}
                                onPlaceSelect={() => {}}
                                onPolygonEdit={(_place, newPaths) => setDraftCoords(newPaths)}
                                onMarkerDragEnd={(index, newPosition) => {
                                    setDraftCoords((prev) => prev.map((p, i) => (i === index ? newPosition : p)));
                                }}
                                onMarkerClick={(index) => {
                                    // Remove a point (keep minimum constraints enforced on accept)
                                    setDraftCoords((prev) => prev.filter((_, i) => i !== index));
                                }}
                            />
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                                Vælg et forslag i listen.
                            </div>
                        )}
                    </div>

                    <div className="min-h-0 overflow-auto p-3 space-y-4">
                        {selected && (
                            <>
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900">Redigér før godkendelse</h3>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Tip: Hvis “Godkend” fejler, er det typisk fordi et felt ikke matcher korrekt format.
                                    </p>
                                </div>
                                {draft && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <label className="text-xs text-gray-700">
                                    Navn
                                    <input
                                        value={draft.name}
                                        onChange={(e) => setDraft((d) => (d ? { ...d, name: e.target.value } : d))}
                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                        placeholder="Navn"
                                    />
                                </label>
                                <label className="text-xs text-gray-700">
                                    Adresse
                                    <input
                                        value={draft.address}
                                        onChange={(e) => setDraft((d) => (d ? { ...d, address: e.target.value } : d))}
                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                        placeholder="Adresse"
                                    />
                                </label>
                                <label className="text-xs text-gray-700">
                                    Type
                                    <select
                                        value={draft.parking_type}
                                        onChange={(e) => setDraft((d) => (d ? { ...d, parking_type: e.target.value } : d))}
                                        className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                                    >
                                        <option value="free">free</option>
                                        <option value="limited_time">limited_time</option>
                                        <option value="paid">paid</option>
                                    </select>
                                            <p className="mt-1 text-[11px] text-gray-500">Eksempel: free / limited_time / paid</p>
                                </label>
                                <label className="text-xs text-gray-700">
                                    Kapacitet (spaces)
                                    <input
                                        value={draft.spaces}
                                        onChange={(e) => setDraft((d) => (d ? { ...d, spaces: e.target.value } : d))}
                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                        placeholder="Fx 120"
                                        inputMode="numeric"
                                    />
                                            <p className="mt-1 text-[11px] text-gray-500">Eksempel: 120</p>
                                </label>
                                <label className="text-xs text-gray-700 md:col-span-2 min-w-0">
                                    cleaned_price_zone
                                    <span className="text-gray-400"> (additionalProperties.cleaned_price_zone)</span>
                                    <input
                                        value={draft.cleanedPriceZone}
                                        onChange={(e) => setDraft((d) => (d ? { ...d, cleanedPriceZone: e.target.value } : d))}
                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                        placeholder='Fx 8 DKK / hr'
                                    />
                                    <p className="mt-1 text-[11px] text-gray-500 whitespace-normal break-words">
                                        Eksempel: <span className="font-mono">8 DKK / hr</span> eller{' '}
                                        <span className="font-mono">
                                            DKK 14/1st hr; DKK 12/2nd hr; DKK 10/3rd hr; DKK 8/4th hr; DKK 52/day
                                        </span>
                                    </p>
                                </label>
                                <label className="text-xs text-gray-700 md:col-span-2 min-w-0">
                                    paid_hours
                                    <span className="text-gray-400"> (additionalProperties.paid_hours)</span>
                                    <input
                                        value={draft.paidHours}
                                        onChange={(e) => setDraft((d) => (d ? { ...d, paidHours: e.target.value } : d))}
                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                        placeholder="Fx 08:00-18:00"
                                    />
                                    <p className="mt-1 text-[11px] text-gray-500">
                                        Eksempel: <span className="font-mono">08:00-18:00</span>
                                    </p>
                                </label>
                                <label className="text-xs text-gray-700 md:col-span-2">
                                    Gratis regler (additionalProperties.free_duration_rules)
                                    <input
                                        value={draft.freeDurationRules}
                                        onChange={(e) => setDraft((d) => (d ? { ...d, freeDurationRules: e.target.value } : d))}
                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                        placeholder="Fx 1 hrs 08:00-18:00 (mon-fri); 1 hrs 08:00-14:00 (sat)"
                                    />
                                            <p className="mt-1 text-[11px] text-gray-500">
                                                Eksempel: <span className="font-mono">1 hrs 08:00-22:00 (mon-sun)</span>
                                            </p>
                                </label>
                                <label className="text-xs text-gray-700 md:col-span-2">
                                    Restriktion (restriction_description)
                                    <input
                                        value={draft.restriction_description}
                                        onChange={(e) =>
                                            setDraft((d) => (d ? { ...d, restriction_description: e.target.value } : d))
                                        }
                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                        placeholder="Fx Kun beboere"
                                    />
                                            <p className="mt-1 text-[11px] text-gray-500">Eksempel: Kun beboere</p>
                                </label>
                                <label className="text-xs text-gray-700 md:col-span-2">
                                    Kommentar
                                    <textarea
                                        value={draft.comment}
                                        onChange={(e) => setDraft((d) => (d ? { ...d, comment: e.target.value } : d))}
                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                        rows={3}
                                        placeholder="Kommentar"
                                    />
                                </label>
                            </div>
                                )}

                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900">Metadata-diff</h3>
                                    {metaRows.length === 0 ? (
                                        <p className="text-sm text-gray-500">Ingen metadata-ændringer (kun geometri).</p>
                                    ) : (
                                        <div className="overflow-auto">
                                            <table className="min-w-full text-sm">
                                                <thead className="text-left text-gray-500">
                                                    <tr>
                                                        <th className="py-1 pr-3 font-medium">Felt</th>
                                                        <th className="py-1 pr-3 font-medium">Prod</th>
                                                        <th className="py-1 pr-3 font-medium">Forslag</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="text-gray-800">
                                                    {metaRows.map((r) => (
                                                        <tr key={r.label} className="border-t border-gray-100">
                                                            <td className="py-1 pr-3 font-medium whitespace-nowrap">{r.label}</td>
                                                            <td className="py-1 pr-3 text-gray-600">{r.from}</td>
                                                            <td className="py-1 pr-3">{r.to}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ParkingLotFeedbackTab;
